/**
 * FacebookAdMockup Component
 *
 * High-fidelity Facebook Feed ad mockup
 * Matches Facebook's current UI (2025)
 */

import React, { useState } from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupHeader } from '../base/AdMockupHeader';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';
import { AdMockupEngagement } from '../base/AdMockupEngagement';
import { FacebookAdMockupProps } from '../types/platform.types';

export const FacebookAdMockup: React.FC<FacebookAdMockupProps> = ({
  headline,
  headlines,
  primaryText,
  primaryTexts,
  description,
  descriptions,
  mediaUrl,
  callToAction = 'Learn More',
  linkUrl = 'yourbrand.com',
  brandName = 'Your Brand',
  brandLogoUrl,
  showEngagement = true,
}) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  // Support both singular and plural props for backward compatibility
  const allPrimaryTexts = primaryTexts || (primaryText ? [primaryText] : []);
  const allHeadlines = headlines || (headline ? [headline] : []);
  const allDescriptions = descriptions || (description ? [description] : []);

  // Use first primary text for display, or show all if expanded
  // Strip MEDIA tags from text before display (images shown separately below)
  const displayText = stripMediaTags(allPrimaryTexts[0] || '');
  const truncatedText = displayText.length > 200 && !isTextExpanded
    ? displayText.slice(0, 200)
    : displayText;
  const showSeeMore = displayText.length > 200;

  return (
    <AdMockupContainer platform="facebook">
      <div className="bg-white dark:bg-card-darkAlt rounded-lg border border-gray-200 dark:border-border-dark overflow-hidden shadow-sm max-w-xl mx-auto">

        {/* Post Header */}
        <AdMockupHeader
          brandName={brandName}
          brandLogo={brandLogoUrl}
          platform="facebook"
          sponsored={true}
        />

        {/* Post Text */}
        {displayText && (
          <div className="px-4 pb-3">
            <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
              {truncatedText}
              {showSeeMore && !isTextExpanded && (
                <>
                  {'... '}
                  <button
                    onClick={() => setIsTextExpanded(true)}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium transition-colors"
                  >
                    See more
                  </button>
                </>
              )}
            </p>

            {/* Show other primary text variations if available */}
            {allPrimaryTexts.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-border-dark">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                  📝 {allPrimaryTexts.length - 1} Additional Text Variation{allPrimaryTexts.length > 2 ? 's' : ''}:
                </p>
                {allPrimaryTexts.slice(1, 3).map((text, idx) => {
                  const cleanedText = stripMediaTags(text);
                  return (
                    <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-2 pl-3 border-l-2 border-blue-300 dark:border-blue-600">
                      {cleanedText.length > 100 ? `${cleanedText.slice(0, 100)}...` : cleanedText}
                    </p>
                  );
                })}
                {allPrimaryTexts.length > 3 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    +{allPrimaryTexts.length - 3} more variations...
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Media */}
        {mediaUrl && (
          <AdMockupMedia
            mediaUrl={mediaUrl}
            aspectRatio="4:5"
            platform="facebook"
            alt="Ad image"
          />
        )}

        {/* Link Preview Card */}
        <div className="p-4 bg-gray-50 dark:bg-card-darkAlt border-t border-gray-200 dark:border-border-dark">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">
            {linkUrl}
          </p>

          {/* Show first headline prominently */}
          {allHeadlines.length > 0 && (
            <p className="font-semibold text-sm text-gray-900 dark:text-white mb-1 line-clamp-2">
              {allHeadlines[0]}
            </p>
          )}

          {/* Show additional headlines */}
          {allHeadlines.length > 1 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                📌 {allHeadlines.length - 1} More Headline{allHeadlines.length > 2 ? 's' : ''}:
              </p>
              {allHeadlines.slice(1, 3).map((h, idx) => (
                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-1 pl-2 border-l-2 border-primary/30">
                  {h}
                </p>
              ))}
              {allHeadlines.length > 3 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  +{allHeadlines.length - 3} more...
                </p>
              )}
            </div>
          )}

          {/* Show first description */}
          {allDescriptions.length > 0 && (
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
              {allDescriptions[0]}
            </p>
          )}

          {/* Show additional descriptions */}
          {allDescriptions.length > 1 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                📝 {allDescriptions.length - 1} More Description{allDescriptions.length > 2 ? 's' : ''}:
              </p>
              {allDescriptions.slice(1, 2).map((d, idx) => (
                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
                  {d}
                </p>
              ))}
              {allDescriptions.length > 2 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  +{allDescriptions.length - 2} more...
                </p>
              )}
            </div>
          )}

          {/* CTA Button */}
          <AdMockupCTA
            text={callToAction}
            platform="facebook"
            variant="primary"
            fullWidth={true}
          />
        </div>

        {/* Engagement Buttons */}
        {showEngagement && (
          <AdMockupEngagement platform="facebook" showCounts={false} />
        )}
      </div>
    </AdMockupContainer>
  );
};

export default FacebookAdMockup;
